import { Injectable } from '@angular/core';

import type { Maybe } from '@plugins/shared/types';
import { isNullish } from '@plugins/shared/utils/assertFunctions';

import {
  type AnimationVariants,
  animationVariantsCatchValue,
} from '../validator';

/** Provide cached validation that passed strings satisfy
 * `AnimationVariants` type constraint. */
@Injectable({ providedIn: 'root' })
export class ValidateAnimationTypeService {
  // Fallback value when dealing with nullish parameters
  protected readonly fallback = 'no-animation' satisfies AnimationVariants;
  // Instantiated validator that defaults to the fallback value
  protected readonly validator = animationVariantsCatchValue(this.fallback);
  protected cache = new Map<string, AnimationVariants>();

  /** Parse potentially nullish string and rely on cache
   * entries. */
  validate(val: Maybe<string>): AnimationVariants {
    if (isNullish(val)) return this.fallback;
    const cached = this.cache.get(val);
    if (cached) return cached;
    const validated = this.parseString(val);
    this.cache.set(val, validated);
    return validated;
  }

  /** Parse string without caching. Prefer the `validate` method. */
  parseString(val: Maybe<string>): AnimationVariants {
    return this.validator.parse(val);
  }
}
